import React, { useState } from 'react';
import { Box, Button, Collapse, Stack } from '@mui/material';
import {
  Add as AddIcon,
  CheckCircleOutlined as ShownIcon,
  DoNotDisturbAltOutlined as HiddenIcon,
  ScienceOutlined as TestIcon,
  ExpandMore as ExpandIcon,
} from '@mui/icons-material';
import { AgronodTypography } from '@agronod/mui-components';

import type { FilterCondition, FilterRule } from '@/types/farm-profile';
import { defaultOperatorFor, defaultValueFor } from '@/constants/farm-profile/filter-rule';
import { describeRule } from '@/utils/farm-profile/describeRule';
import { deriveConditionReason } from '@/utils/farm-profile/deriveReason';
import FilterConditionRow from './FilterConditionRow';
import FilterRulePreview from './FilterRulePreview';

interface FilterRuleEditorProps {
  /** Current rule value, null when no rule is configured. */
  value: FilterRule | null;
  /** Called with the updated rule. Passes null when the last condition is removed. */
  onChange: (next: FilterRule | null) => void;
}

/**
 * FilterRuleEditor — plain-language editor for a filterRule.
 *
 * Design: the admin never reasons backwards from booleans to visibility.
 * 1. A fixed header states the mental model ("Visa kravet bara för gårdar som …").
 * 2. Each condition reads as a sentence clause (see FilterConditionRow).
 * 3. A live echo restates, in plain Swedish, exactly who sees and who doesn't.
 * 4. An optional interactive preview (collapsed by default) tests a mock profile.
 *
 * Semantics: ALL conditions must pass (AND) for the requirement to be visible.
 */
const FilterRuleEditor: React.FC<FilterRuleEditorProps> = ({ value, onChange }) => {
  const conditions = value?.conditions ?? [];
  const [previewOpen, setPreviewOpen] = useState(false);

  /** Default field for new conditions — productionTypes is the most common use case. */
  const DEFAULT_FIELD: FilterCondition['field'] = 'productionTypes';

  const buildEmptyCondition = (): FilterCondition => ({
    field: DEFAULT_FIELD,
    operator: defaultOperatorFor(DEFAULT_FIELD),
    value: defaultValueFor(DEFAULT_FIELD),
  });

  const addCondition = () => {
    onChange({ conditions: [...conditions, buildEmptyCondition()] });
  };

  const updateAt = (index: number, next: FilterCondition) => {
    onChange({ conditions: conditions.map((c, i) => (i === index ? next : c)) });
  };

  const removeAt = (index: number) => {
    const next = conditions.filter((_, i) => i !== index);
    onChange(next.length > 0 ? { conditions: next } : null);
  };

  const description = describeRule(value);
  // The farmer-facing hide message: admin override on the first condition, else derived.
  const firstCondition = conditions[0];
  const hideMessage = firstCondition
    ? (firstCondition.reason && firstCondition.reason.trim().length > 0
        ? firstCondition.reason
        : deriveConditionReason(firstCondition))
    : '';

  return (
    <Stack spacing={2}>
      {/* Header — sets the mental model */}
      <AgronodTypography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
        Visa det här kravet bara för gårdar som matchar alla villkor nedan:
      </AgronodTypography>

      {/* Conditions list */}
      {conditions.length === 0 ? (
        <AgronodTypography
          variant="body2"
          sx={{
            color: "text.secondary",
            fontStyle: 'italic'
          }}>
          Inga villkor ännu — kravet visas för alla gårdar. Lägg till ett villkor för att begränsa.
        </AgronodTypography>
      ) : (
        <Box>
          {conditions.map((condition, index) => (
            <FilterConditionRow
              key={index}
              value={condition}
              onChange={(next) => updateAt(index, next)}
              onRemove={() => removeAt(index)}
            />
          ))}
        </Box>
      )}

      <Box>
        <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addCondition}>
          Lägg till villkor
        </Button>
      </Box>

      {/* Live echo — restates the effect in plain Swedish */}
      {description.hasConditions && (
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'grey.200',
          }}
        >
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} sx={{
              alignItems: "flex-start"
            }}>
              <ShownIcon sx={{ fontSize: 18, color: 'success.main', mt: '1px', flexShrink: 0 }} />
              <AgronodTypography variant="body2" sx={{ color: 'text.primary' }}>
                <strong>Visas</strong> för gårdar som {description.shownFor}.
              </AgronodTypography>
            </Stack>
            <Stack direction="row" spacing={1} sx={{
              alignItems: "flex-start"
            }}>
              <HiddenIcon sx={{ fontSize: 18, color: 'text.disabled', mt: '1px', flexShrink: 0 }} />
              <AgronodTypography variant="body2" sx={{
                color: "text.secondary"
              }}>
                <strong>Döljs</strong> för övriga gårdar
                {hideMessage && (
                  <> — de ser: <em>"{hideMessage}"</em></>
                )}
              </AgronodTypography>
            </Stack>
          </Stack>
        </Box>
      )}

      {/* Interactive preview — collapsed by default to keep the editor calm */}
      {description.hasConditions && (
        <Box>
          <Button
            variant="text"
            size="small"
            startIcon={<TestIcon sx={{ fontSize: 16 }} />}
            endIcon={
              <ExpandIcon
                sx={{
                  fontSize: 18,
                  transform: previewOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 160ms',
                }}
              />
            }
            onClick={() => setPreviewOpen((v) => !v)}
            sx={{ color: 'text.secondary' }}
          >
            Testa mot en exempelprofil
          </Button>
          <Collapse in={previewOpen} timeout={300}>
            <FilterRulePreview rule={value} />
          </Collapse>
        </Box>
      )}
    </Stack>
  );
};

export default FilterRuleEditor;
